/**
 * usePreScanActions — per-symbol action handlers for the missing symbols pre-scan banner.
 *
 * Exported actions:
 *   handleFixNow(sym)        — build synthetic finding → navigate to Fix & Continue
 *   handleMarkExternal(sym)  — persist EXTERNAL_DEPENDENCY decision (no rerun)
 *   handleStub(sym)          — persist GENERATE_COMPILE_STUB decision + trigger compile
 *                              rerun (Phase 2.5 creates the stub) + poll + refresh
 *   resetStates(syms, existingDecisions) — seed actionStates from DB decisions on mount
 *
 * Action state per symbol:
 *   null         — no action taken yet (all 3 buttons enabled)
 *   'saving_ext' — markExternal in flight
 *   'external'   — persisted as EXTERNAL_DEPENDENCY
 *   'stubbing'   — stub decision saved; rerun in progress
 *   'stubbed'    — rerun completed (stub was created by Phase 2.5)
 *   'error'      — last action failed
 */

import { useState, useCallback, useRef } from 'react'
import { upsertDecision } from '../services/gates/gateDecisionService'

// ── Auth helper ────────────────────────────────────────────────────────────────

function authHeaders(extra = {}) {
  const token = localStorage.getItem('modernizeit_token')
  return token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra }
}

// ── Fingerprint — must match GateReportView.preScanFingerprint ─────────────────

function djb2(str) {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i)
    hash |= 0
  }
  return (hash >>> 0).toString(16)
}

function preScanFingerprint(symbol) {
  return djb2(`pre_scan:compile:java:${symbol}`)
}

// ── Poll rerun until complete ─────────────────────────────────────────────────

async function pollRerun(apiBase, jobId, abortRef) {
  const statusUrl = `${apiBase}/pre-flight/status`
  for (let i = 0; i < 270; i++) {          // 9 min max (2 s × 270)
    await new Promise(r => setTimeout(r, 2000))
    if (abortRef?.current) return jobId     // cancelled
    try {
      const res = await fetch(`${statusUrl}/${jobId}`, { headers: authHeaders() })
      if (!res.ok) continue
      const st   = await res.json()
      const norm = (st.status || '').toLowerCase()
      if (['completed', 'succeeded', 'failed'].includes(norm)) return jobId
    } catch (_) { /* transient — retry */ }
  }
  return jobId   // timed out — return jobId anyway so caller can refresh
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePreScanActions({
  analysisContext,
  apiBase,
  currentJobId,
  onFixNow,           // (syntheticFinding) => void — called by Fix now
  onRerunComplete,    // (newJobId) => void — called when stub rerun finishes
}) {
  const [actionStates, setActionStates] = useState({})
  // null | 'rerunning' | 'done' | 'error'
  const [rerunStatus, setRerunStatus]   = useState(null)

  const abortRef = useRef(false)

  const accountId = analysisContext?.scout_account_id
  const appName   = analysisContext?.application_name
  const userId    = analysisContext?.user_id

  // ── resetStates — seed from existing DB decisions on banner mount ───────────
  //
  // Called by GateReportView after it fetches both missingSymbols + fetchDecisions.
  // Only marks symbols that appear in both lists — doesn't create phantom states.

  const resetStates = useCallback((syms, existingDecisions) => {
    if (!syms?.length) { setActionStates({}); setRerunStatus(null); return }

    const bySymbol = {}
    for (const d of (existingDecisions || [])) {
      const sym = d.target_symbol
      if (!sym) continue
      if (d.decision_type === 'EXTERNAL_DEPENDENCY') bySymbol[sym] = 'external'
      if (d.decision_type === 'GENERATE_COMPILE_STUB') bySymbol[sym] = 'stubbed'
    }

    const init = {}
    for (const s of syms) {
      init[s.symbol] = bySymbol[s.symbol] || null
    }
    setActionStates(init)
    setRerunStatus(null)
    abortRef.current = false
  }, [])

  // ── Fix now ──────────────────────────────────────────────────────────────────

  const handleFixNow = useCallback((sym) => {
    const syntheticFinding = {
      id:           preScanFingerprint(sym.symbol),
      severity:     'BLOCK',
      state:        'OPEN',
      category:     'Missing Symbol',
      summary:      `Missing symbol: ${sym.symbol}`,
      javaLocation: { file: sym.file, line: sym.line },
    }
    if (import.meta.env.DEV)
      console.log(' [usePreScanActions] handleFixNow →', { sym, syntheticFinding })
    onFixNow?.(syntheticFinding)
  }, [onFixNow])

  // ── Mark external ─────────────────────────────────────────────────────────────

  const handleMarkExternal = useCallback(async (sym) => {
    if (!accountId || !appName) return
    setActionStates(prev => ({ ...prev, [sym.symbol]: 'saving_ext' }))
    try {
      await upsertDecision({
        account_id:       accountId,
        application_name: appName,
        fingerprint:      preScanFingerprint(sym.symbol),
        gate_type:        'compile',
        gate_job_id:      currentJobId,
        decision_type:    'EXTERNAL_DEPENDENCY',
        target_file:      sym.file,
        target_symbol:    sym.symbol,
        patch_json: JSON.stringify({
          strategy:       'external',
          class_name:     sym.symbol.split('.').pop(),
          package:        sym.package,
          missing_symbol: sym.symbol,
        }),
        user_id: userId,
      })
      if (import.meta.env.DEV)
        console.log(' [usePreScanActions] markExternal saved', sym.symbol)
      setActionStates(prev => ({ ...prev, [sym.symbol]: 'external' }))
    } catch (err) {
      console.error('[usePreScanActions] markExternal failed:', err)
      setActionStates(prev => ({ ...prev, [sym.symbol]: 'error' }))
    }
  }, [accountId, appName, currentJobId, userId])

  // ── Shared: upsert a GENERATE_COMPILE_STUB decision for one symbol ────────────

  const _upsertStubDecision = useCallback(async (sym) => {
    await upsertDecision({
      account_id:       accountId,
      application_name: appName,
      fingerprint:      preScanFingerprint(sym.symbol),
      gate_type:        'compile',
      gate_job_id:      currentJobId,
      decision_type:    'GENERATE_COMPILE_STUB',
      target_file:      sym.file,
      target_symbol:    sym.symbol,
      // patch_json includes full FQN + package so DecisionApplier._create_stub_class()
      // can derive the correct package directory without reading the referencing file.
      patch_json: JSON.stringify({
        strategy:       'create_stub_class',
        symbol:         sym.symbol.split('.').pop(),
        class_name:     sym.symbol.split('.').pop(),
        package:        sym.package,
        missing_symbol: sym.symbol,
      }),
      user_id: userId,
    })
  }, [accountId, appName, currentJobId, userId])

  // ── Shared: start compile rerun + poll ────────────────────────────────────────

  const _startAndPollRerun = useCallback(async () => {
    const res = await fetch(`${apiBase}/pre-flight/start`, {
      method:  'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        scout_account_id: accountId,
        application_name: appName,
        user_id:          userId,
        // No overlay_job_id — stub is created via Phase 2.5 gate decisions, not overlays
      }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || `Rerun start failed (${res.status})`)
    }
    const { job_id: newJobId } = await res.json()
    if (import.meta.env.DEV)
      console.log(' [usePreScanActions] rerun started', { newJobId })
    const finalJobId = await pollRerun(apiBase, newJobId, abortRef)
    if (import.meta.env.DEV)
      console.log(' [usePreScanActions] rerun done', { finalJobId })
    return finalJobId
  }, [accountId, appName, userId, apiBase])

  // ── Generate stub (single symbol) ────────────────────────────────────────────
  //
  // Flow: upsert decision → POST /pre-flight/start → poll → refresh

  const handleStub = useCallback(async (sym) => {
    if (!accountId || !appName || !userId) return
    abortRef.current = false
    setActionStates(prev => ({ ...prev, [sym.symbol]: 'stubbing' }))
    setRerunStatus('rerunning')
    try {
      await _upsertStubDecision(sym)
      if (import.meta.env.DEV)
        console.log(' [usePreScanActions] GENERATE_COMPILE_STUB saved', sym.symbol)
      const finalJobId = await _startAndPollRerun()
      setActionStates(prev => ({ ...prev, [sym.symbol]: 'stubbed' }))
      setRerunStatus('done')
      onRerunComplete?.(finalJobId)
    } catch (err) {
      console.error('[usePreScanActions] handleStub failed:', err)
      setActionStates(prev => ({ ...prev, [sym.symbol]: 'error' }))
      setRerunStatus('error')
    }
  }, [accountId, appName, userId, _upsertStubDecision, _startAndPollRerun, onRerunComplete])

  // ── Generate ALL stubs (batch) ────────────────────────────────────────────────
  //
  // Upserts GENERATE_COMPILE_STUB decisions for every unsettled symbol, then triggers
  // ONE compile rerun so Phase 2.5 creates all stubs in a single Maven run.
  //
  // Usage: handleAllStubs(missingSymbols)

  const handleAllStubs = useCallback(async (symbols) => {
    if (!accountId || !appName || !userId) return
    const unsettled = (symbols || []).filter(sym => {
      const st = actionStates[sym.symbol]
      return st === null || st === 'error'
    })
    if (!unsettled.length) return
    abortRef.current = false

    // Mark all unsettled as 'stubbing'
    setActionStates(prev => {
      const next = { ...prev }
      for (const sym of unsettled) next[sym.symbol] = 'stubbing'
      return next
    })
    setRerunStatus('rerunning')

    if (import.meta.env.DEV)
      console.log(' [usePreScanActions] handleAllStubs', { count: unsettled.length })

    const failed = []
    // Upsert all decisions first (no reruns yet)
    for (const sym of unsettled) {
      try {
        await _upsertStubDecision(sym)
        if (import.meta.env.DEV)
          console.log(' [usePreScanActions] upserted stub decision for', sym.symbol)
      } catch (err) {
        console.error('[usePreScanActions] handleAllStubs: upsert failed for', sym.symbol, err)
        failed.push(sym.symbol)
      }
    }

    // Trigger ONE rerun for all decisions
    try {
      const finalJobId = await _startAndPollRerun()
      setActionStates(prev => {
        const next = { ...prev }
        for (const sym of unsettled) {
          next[sym.symbol] = failed.includes(sym.symbol) ? 'error' : 'stubbed'
        }
        return next
      })
      setRerunStatus('done')
      onRerunComplete?.(finalJobId)
    } catch (err) {
      console.error('[usePreScanActions] handleAllStubs: rerun failed', err)
      setActionStates(prev => {
        const next = { ...prev }
        for (const sym of unsettled) next[sym.symbol] = 'error'
        return next
      })
      setRerunStatus('error')
    }
  }, [accountId, appName, userId, actionStates, _upsertStubDecision, _startAndPollRerun, onRerunComplete])

  const isRunning = rerunStatus === 'rerunning'

  return {
    actionStates,
    rerunStatus,
    isRunning,
    handleFixNow,
    handleMarkExternal,
    handleStub,
    handleAllStubs,
    resetStates,
  }
}
